import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import { Save, Image as ImageIcon, FileText, Eye, EyeOff, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Site Page Content Editor — admin can edit the body copy for the About Us
// and Contact Us pages using markdown. Storage piggy-backs on `business_info`
// (keys: `about_us_content`, `contact_us_content`) so no new table is needed.
//
// Markdown is intentionally chosen over a custom block editor: it gives admins
// full control over headings, paragraphs, links, lists, bold/italic, and
// embedded images using a tiny syntax surface, and the same content renders
// cleanly on the public-facing About / Contact pages via react-markdown.
//
// Images: admins can either paste a URL (`![alt](https://...)`) or click the
// Insert Image button, which uploads a file via the existing
// `/api/upload/image` endpoint and inserts the resulting URL at the cursor.

const PAGES = [
  {
    key: 'about_us_content',
    title: 'About Us page',
    placeholder:
      '## Welcome to our shop\n\nWrite your story, mission, and team bios here.\n\n- We have been grooming pets since...\n- Our team specializes in...\n\n![Our shop](https://example.com/your-image.jpg)',
    helpText:
      'Replaces the default About Us body. Leave blank to fall back to the auto-generated welcome message.',
  },
  {
    key: 'contact_us_content',
    title: 'Contact Us page (intro / extra info)',
    placeholder:
      '## Get in touch\n\nWe respond within 24 hours on weekdays.\n\n**Hours:** Mon–Sat, 9am–6pm.\n\n![Storefront](https://example.com/storefront.jpg)',
    helpText:
      'Appears above the auto-generated email / phone / address card on the Contact Us page. Use this for an intro, hours, parking notes, etc.',
  },
];

const MarkdownPageEditor = ({ pageKey, title, placeholder, helpText }) => {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/business-info/${pageKey}`);
        if (!alive) return;
        const v = res.data?.info_value || '';
        setContent(v);
        setOriginalContent(v);
      } catch (err) {
        // 404 / empty is fine — first time editing this page.
        if (!alive) return;
        setContent('');
        setOriginalContent('');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [pageKey]);

  const dirty = content !== originalContent;

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-info/${pageKey}`,
        { value: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOriginalContent(content);
      toast.success(`${title} saved`);
    } catch (err) {
      console.error('Save page content error:', err);
      toast.error(err.response?.data?.error || 'Failed to save page content');
    } finally {
      setSaving(false);
    }
  };

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) {
      setContent((prev) => prev + text);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = content.slice(0, start) + text + content.slice(end);
    setContent(next);
    // Restore cursor right after the inserted text on the next paint.
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const url = res.data?.url;
      if (!url) throw new Error('Upload returned no URL');
      const alt = file.name.replace(/\.[^.]+$/, '');
      insertAtCursor(`\n\n![${alt}](${url})\n\n`);
      toast.success('Image uploaded and inserted');
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset the input so selecting the same file again still triggers onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center text-gray-500">
          <Loader size={16} className="animate-spin mr-2" />
          Loading {title}...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-900 flex items-center">
          <FileText size={18} className="mr-2 text-primary-600" />
          {title}
        </h4>
        <button
          type="button"
          onClick={() => setShowPreview((p) => !p)}
          className="text-xs text-gray-600 hover:text-gray-900 flex items-center"
        >
          {showPreview ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
          {showPreview ? 'Hide preview' : 'Show preview'}
        </button>
      </div>
      <p className="text-xs text-gray-500">{helpText}</p>

      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={placeholder}
        rows={10}
        className="input font-mono text-sm w-full"
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
        >
          {uploading ? <Loader size={14} className="animate-spin mr-1" /> : <ImageIcon size={14} className="mr-1" />}
          {uploading ? 'Uploading...' : 'Insert Image'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <span className="text-xs text-gray-500">
          Markdown supported: <code className="bg-gray-100 px-1 rounded">**bold**</code>{' '}
          <code className="bg-gray-100 px-1 rounded">## heading</code>{' '}
          <code className="bg-gray-100 px-1 rounded">[link](url)</code>{' '}
          <code className="bg-gray-100 px-1 rounded">![alt](image-url)</code>
        </span>
        <div className="ml-auto">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className={`inline-flex items-center px-4 py-1.5 text-sm rounded ${
              dirty
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            } disabled:opacity-50`}
          >
            {saving ? <Loader size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            {saving ? 'Saving...' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      {showPreview && (
        <div className="mt-2 p-4 border border-dashed border-gray-300 rounded bg-gray-50">
          <div className="text-xs uppercase text-gray-500 mb-2">Preview</div>
          {content.trim() ? (
            <div className="prose max-w-none text-gray-800">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">
              Empty — the public page will fall back to its default content.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const SitePageContentEditor = () => {
  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <FileText className="text-primary-600" size={24} />
        <h3 className="text-xl font-bold text-gray-900">Static Page Content</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Edit the body copy for your About Us and Contact Us pages. Use markdown
        for headings, paragraphs, lists, bold / italic, links, and embedded
        images. Click <em>Insert Image</em> to upload a file from your
        computer — the public-facing page will display it inline.
      </p>
      <div className="space-y-4">
        {PAGES.map((p) => (
          <MarkdownPageEditor
            key={p.key}
            pageKey={p.key}
            title={p.title}
            placeholder={p.placeholder}
            helpText={p.helpText}
          />
        ))}
      </div>
    </div>
  );
};

export default SitePageContentEditor;
